import { defineSlotRecipe } from '@chakra-ui/react';

import { focusRing } from '../focusRing';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/toggles.html
 *
 * 16×16, 4px radius, 1.5px border tGray.lightPurple (#C8C5D4).
 * Checked: indigo fill + indigo border + white checkmark.
 * Indeterminate: indigo fill + white bar.
 *
 * v3 port notes:
 * - Slots renamed from v2's `checkboxAnatomy`: `container` → `root`,
 *   `icon` → `indicator`. `control` and `label` keep their names.
 * - v2 spaced the label with `marginInlineStart: 2`. v3's `root` is a flex
 *   container with a `gap`, so that margin is dropped — it would stack on top
 *   of the gap. The built-in recipe scales `gap` with size (2/2.5/3); v2's
 *   margin was size-independent, so each size below pins `gap: 2` (8px) to
 *   preserve the existing spacing exactly.
 * - `Checkbox.Control` auto-renders `Checkbox.Indicator` via `defaultProps`,
 *   and that indicator is an *unstyled* `currentColor` SVG — so `indicator`
 *   must set an explicit size. v2's tick was a `1.2em`-wide, 12×10-viewBox
 *   polyline with a 2-unit stroke (about 9×8px, 2px stroke at the default
 *   size); v3's is a 24-unit viewBox with a 3-unit stroke, so carrying v2's
 *   `fontSize` px over as a `boxSize` shrank it to about 7×5px with a 1.25px
 *   stroke. Filling the control's content box with a 3.5-unit stroke matches
 *   v2's tick at every size.
 * - Chakra's built-in `checkbox` recipe defaults to `variant: 'solid'` and
 *   deep-merges *under* this one (`mergeConfigs` in createSystem), so a call
 *   site that passes no variant still gets the built-in `solid` styles layered
 *   on top of `base`. `solid` is therefore redefined below, restating the
 *   design-system treatment on exactly the keys the built-in sets — otherwise
 *   `border.emphasized` and `colorPalette.solid` leak through as the resting
 *   border and checked fill.
 */

/**
 * Checked/indeterminate fill, shared by both states and by the redefined
 * `solid` variant. Exported so `RadioGroup.ts` doesn't restate the disabled
 * treatment (the only part the two marks share once radio drops the fill).
 */
const checkedMarkStyle = {
  bg: 'tIndigo.base',
  borderColor: 'tIndigo.base',
  color: 'white',
  _disabled: {
    background: 'fg.disabled',
    borderColor: 'fg.disabled',
    color: 'white',
  },
};

/**
 * Resting appearance shared by the checkbox control and the radio item
 * control: same 16×16 footprint, same 1.5px lightPurple border on a surface
 * background, same focus halo. Only the radius and the checked treatment
 * differ between the two, so those stay in the respective recipes.
 */
export const sharedMarkBase = {
  borderWidth: '1.5px',
  borderColor: 'tGray.lightPurple',
  bg: 'bg.surface',
  // The built-in checkmark/radiomark styles paint an `outline`-based ring on
  // focus. Turn it off so the design-system `boxShadow` halo below is the only
  // focus affordance (v3's `focusVisibleRing: 'none'` emits `outline: none`).
  focusVisibleRing: 'none',
  _focusVisible: focusRing(),
  _disabled: {
    background: 'bg.muted',
    borderColor: 'fg.disabled',
    // The built-in checkmark/radiomark fade a disabled control to 50%. v2 kept
    // it opaque (only its label dimmed, and the app set that back to 1).
    opacity: 1,
  },
};

const controlBase = {
  ...sharedMarkBase,
  // The built-in checkmark uses the `checkbox` cursor token, which v3 maps to
  // `default`; v2 checkboxes showed a pointer.
  cursor: 'pointer',
  borderRadius: 'sm', // 4px
  _checked: {
    ...checkedMarkStyle,
    _hover: {
      bg: 'tIndigo.600',
      borderColor: 'tIndigo.600',
    },
  },
  _indeterminate: checkedMarkStyle,
};

const labelBase = {
  // Optical alignment: `root` aligns to `flex-start` so multi-line labels sit
  // against the top of the control rather than centering the whole row.
  mt: '-1px',
  lineHeight: '18px',
  // The built-in label is `medium`; v2 labels were normal weight.
  fontWeight: 'normal',
  color: 'fg.primary',
  _disabled: {
    opacity: 1,
    color: 'fg.muted',
  },
};

/** Label treatment shared by the `select-events` / `custom-select` / `unsaved` variants. */
const emphasizedLabel = {
  color: 'fg.strong',
  fontWeight: 'medium',
};

export const checkboxTheme = defineSlotRecipe({
  slots: ['root', 'control', 'label', 'indicator', 'group'],
  base: {
    root: {
      alignItems: 'flex-start',
      gap: 2,
      cursor: 'pointer',
      _disabled: { cursor: 'not-allowed' },
    },
    control: controlBase,
    label: labelBase,
    indicator: { boxSize: 'full', strokeWidth: '3.5' },
  },
  variants: {
    variant: {
      'select-events': {
        label: emphasizedLabel,
      },
      'custom-select': {
        label: {
          ...emphasizedLabel,
          fontWeight: 'normal',
          w: '100%',
        },
      },
      unsaved: {
        label: emphasizedLabel,
        control: {
          // tOrange.base at 25%, as a glow rather than a border so it reads
          // on a 16px control. Kept as literal rgba (matching v2) because the
          // value is one blended color, not a token reference.
          boxShadow: '0 0 0 10px rgba(228, 121, 21, 0.25)',
          clipPath: 'circle(100%)',
          backgroundColor: 'rgba(228, 121, 21, 0.25)',
        },
      },
      // Redefines the built-in `solid` variant — see the header note. These
      // are the exact keys `checkmarkRecipe.variants.variant.solid` sets.
      solid: {
        control: {
          borderColor: 'tGray.lightPurple',
          '&:is([data-state=checked], [data-state=indeterminate])':
            checkedMarkStyle,
        },
      },
    },
    // Each label restates `lineHeight`: the built-in size's `textStyle` (20px
    // line height) sits in the same variant and would otherwise beat the
    // base's 18px, knocking the `mt: -1px` optical alignment off.
    size: {
      sm: {
        root: { gap: 2 },
        control: { boxSize: 4 }, // 16px — design system default
        label: { fontSize: 'sm', lineHeight: '18px' }, // 13px
      },
      md: {
        root: { gap: 2 },
        control: { boxSize: 5 },
        label: { fontSize: 'sm', lineHeight: '18px' },
      },
      lg: {
        root: { gap: 2 },
        control: { boxSize: 6 },
        label: { fontSize: 'lg', lineHeight: '18px' },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export default checkboxTheme;
